/**
 * Route tree base (code-based TanStack Router). Areas hang their routes under `staffRoute` (signed-in shell)
 * or `publicRoute` (sign-in pages) from src/areas/<area>/routes.tsx; app/router.tsx assembles the tree.
 *
 * Guards never get disabled, in dev either: the dev navigator changes the mock WORLD (who is signed in,
 * what state a dossier is in) and the real guards run on it.
 */
import { createRootRouteWithContext, createRoute, Outlet, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { roleCan, type Capability } from "@/contracts/permissions";
import type { StaffSession } from "@/contracts/staff-auth";
import { sessionQuery } from "@/auth/session";
import { homeFor } from "@/shell/nav";
import { Shell } from "@/shell/shell";
import { ForbiddenPage, NotFoundPage, RouteErrorPage, ShellNotFound } from "@/ui/page";

export interface RouterContext {
  queryClient: QueryClient;
}

/** Search params as plain strings until an area tightens its route with a Zod schema. */
export function looseSearch(search: Record<string, unknown>): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const [k, v] of Object.entries(search)) if (v !== undefined && v !== null) out[k] = String(v);
  return out;
}

export class ForbiddenError extends Error {
  constructor(public cap: Capability) {
    super("Droits insuffisants pour cette action.");
    this.name = "ForbiddenError";
  }
}

/** beforeLoad guard: the signed-in role needs `mode` on `cap` (matrix in contracts/permissions.ts), else the 403 page. */
export function guard(cap: Capability, mode: "read" | "write" = "read") {
  return ({ context }: { context: { session?: StaffSession } }) => {
    const s = context.session;
    if (!s || !roleCan(s.user.role, cap, mode)) throw new ForbiddenError(cap);
  };
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFoundPage,
});

/** Sign-in pages: a signed-in user goes to their home instead. */
export const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "public",
  beforeLoad: async ({ context, search }) => {
    const s = await context.queryClient.ensureQueryData(sessionQuery);
    const retour = (search as Record<string, unknown>).retour;
    if (s) throw redirect({ href: typeof retour === "string" && retour.startsWith("/") ? retour : homeFor(s.user.role) });
  },
  component: Outlet,
});

/** Everything behind a staff session, inside the shell. */
export const staffRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "staff",
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery);
    if (!session) throw redirect({ to: "/connexion", search: { retour: location.href } });
    return { session };
  },
  component: Shell,
  // Inside the shell: a page that does not exist keeps the sidebar.
  notFoundComponent: ShellNotFound,
});

/** Router default: errors of a page render in the shell's outlet (403 card for a refused permission). */
export function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  return error instanceof ForbiddenError ? <ForbiddenPage /> : <RouteErrorPage error={error} reset={reset} />;
}

export const indexRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/",
  beforeLoad: ({ context }) => {
    throw redirect({ href: homeFor(context.session.user.role) });
  },
});

export const referenceIndexRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/reference",
  beforeLoad: () => {
    throw redirect({ to: "/reference/roles" });
  },
});

export const creationIndexRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation",
  beforeLoad: ({ context }) => {
    const r = context.session.user.role;
    throw redirect({ href: r === "AGENT" ? "/creation/a-faire" : r === "ADMIN" ? "/creation/nouvelles" : "/creation/lecture" });
  },
});
